import { getDataSource } from '@/lib/data-source';
import type { RequestConfig } from '@/lib/data-source/types';

export type SocialOAuthProvider = 'google' | 'github' | 'apple' | 'azure';
export type SocialOAuthMode = 'test' | 'live';
export type SocialOAuthValidationStatus = 'valid' | 'invalid' | 'pending' | 'unvalidated';

export interface SocialOAuthSettingsListItem {
  provider: SocialOAuthProvider;
  enabled: boolean;
  mode?: SocialOAuthMode;
  client_id_masked?: string | null;
  last_validated_at?: string | null;
  validation_status?: SocialOAuthValidationStatus;
  issues?: string[] | null;
  redirect_uri?: string | null;
  apple_config_flags?: {
    has_team_id?: boolean;
    has_key_id?: boolean;
    has_private_key?: boolean;
  } | null;
  meta?: {
    correlation_id?: string;
    message?: string;
  };
}

export interface SocialOAuthSettingsDetail extends SocialOAuthSettingsListItem {
  scopes?: string[] | null;
  redirect_uri_override?: string | null;
  tenant_config?: Record<string, unknown> | null;
  login_hint_default?: string | null;
  prompt?: string | null;
  has_client_secret?: boolean;
  has_private_key?: boolean;
  validation_metadata?: Record<string, unknown> | null;
  created_at?: string | null;
  updated_at?: string | null;
  created_by?: {
    id: string;
    name: string;
  } | null;
  updated_by?: {
    id: string;
    name: string;
  } | null;
  meta?: {
    correlation_id?: string;
    message?: string;
  };
}

export interface SocialOAuthSettingsRequest {
  enabled?: boolean;
  mode?: SocialOAuthMode;
  client_id?: string;
  client_secret?: string;
  scopes?: string[];
  login_hint_default?: string;
  prompt?: string;
  redirect_uri_override?: string;
  tenant_config?: Record<string, unknown>;
  /**
   * Apple specific fields
   */
  team_id?: string;
  key_id?: string;
  private_key?: string;
  auto_rotate_secret?: boolean;
  /**
   * GitHub specific
   */
  org_whitelist?: string[];
  enterprise_host?: string;
  /**
   * Allow arbitrary backend-supported fields
   */
  [key: string]: unknown;
}

export interface SocialOAuthValidationResponse {
  data?: {
    valid?: boolean;
    success?: boolean;
    message?: string | null;
    meta?: Record<string, unknown>;
  };
  meta?: {
    message?: string;
    correlation_id?: string;
  };
}

class SocialOAuthSettingsService {
  private dataSource = getDataSource();

  private withProviderScope(config: RequestConfig = {}): RequestConfig {
    return {
      ...config,
      headers: {
        ...(config.headers ?? {}),
        'X-Florentin-Auth-Scope': 'provider',
      },
    };
  }

  async listSettings(): Promise<SocialOAuthSettingsListItem[]> {
    const response = await this.dataSource.get<{
      settings?: SocialOAuthSettingsListItem[];
      data?: {
        settings?: SocialOAuthSettingsListItem[];
      };
    }>(
      '/api/v1/oauth/settings',
      this.withProviderScope(),
    );

    const extractFrom = (payload: unknown): SocialOAuthSettingsListItem[] => {
      if (!payload) {
        return [];
      }

      if (Array.isArray(payload)) {
        return payload as SocialOAuthSettingsListItem[];
      }

      if (typeof payload !== 'object') {
        return [];
      }

      const objectPayload = payload as {
        settings?: unknown;
        data?: unknown;
      };

      if (Array.isArray(objectPayload.settings)) {
        return objectPayload.settings as SocialOAuthSettingsListItem[];
      }

      if (objectPayload.data) {
        return extractFrom(objectPayload.data);
      }

      return [];
    };

    return extractFrom(response.data);
  }

  async getSettings(provider: SocialOAuthProvider): Promise<SocialOAuthSettingsDetail> {
    const response = await this.dataSource.get<{
      setting?: SocialOAuthSettingsDetail;
      data?: SocialOAuthSettingsDetail | { setting?: SocialOAuthSettingsDetail };
    }>(
      `/api/v1/oauth/settings/${provider}`,
      this.withProviderScope(),
    );

    const extractFrom = (payload: unknown): SocialOAuthSettingsDetail | null => {
      if (!payload || typeof payload !== 'object') {
        return null;
      }

      if ('provider' in payload) {
        return payload as SocialOAuthSettingsDetail;
      }

      if ('setting' in payload) {
        return (payload as { setting?: SocialOAuthSettingsDetail }).setting ?? null;
      }

      if ('data' in payload) {
        return extractFrom((payload as { data?: unknown }).data);
      }

      return null;
    };

    const detail = extractFrom(response.data);
    if (detail) {
      return detail;
    }

    throw new Error('Invalid social OAuth settings payload');
  }

  async createSettings(
    provider: SocialOAuthProvider,
    payload: SocialOAuthSettingsRequest,
  ): Promise<SocialOAuthSettingsDetail> {
    const response = await this.dataSource.post<{
      data?: SocialOAuthSettingsDetail | { setting?: SocialOAuthSettingsDetail };
    }>(
      `/api/v1/oauth/settings/${provider}`,
      payload,
      this.withProviderScope(),
    );
    const payloadData = response.data?.data;
    if (payloadData && typeof payloadData === 'object' && 'setting' in payloadData) {
      return (payloadData as { setting: SocialOAuthSettingsDetail }).setting;
    }
    if (payloadData) {
      return payloadData as SocialOAuthSettingsDetail;
    }
    return response.data as unknown as SocialOAuthSettingsDetail;
  }

  async updateSettings(
    provider: SocialOAuthProvider,
    payload: SocialOAuthSettingsRequest,
  ): Promise<SocialOAuthSettingsDetail> {
    const response = await this.dataSource.put<{
      data?: SocialOAuthSettingsDetail | { setting?: SocialOAuthSettingsDetail };
    }>(
      `/api/v1/oauth/settings/${provider}`,
      payload,
      this.withProviderScope(),
    );

    const payloadData = response.data?.data;
    if (payloadData && typeof payloadData === 'object' && 'setting' in payloadData) {
      return (payloadData as { setting: SocialOAuthSettingsDetail }).setting;
    }
    if (payloadData) {
      return payloadData as SocialOAuthSettingsDetail;
    }
    return response.data as unknown as SocialOAuthSettingsDetail;
  }

  async validateSettings(
    provider: SocialOAuthProvider,
    payload?: SocialOAuthSettingsRequest,
  ): Promise<SocialOAuthValidationResponse> {
    const response = await this.dataSource.post<SocialOAuthValidationResponse>(
      `/api/v1/oauth/settings/${provider}/validate`,
      payload,
      this.withProviderScope(),
    );
    return response.data;
  }

  async testSettings(
    provider: SocialOAuthProvider,
    payload?: SocialOAuthSettingsRequest,
  ): Promise<SocialOAuthValidationResponse> {
    const response = await this.dataSource.post<SocialOAuthValidationResponse>(
      `/api/v1/oauth/settings/${provider}/test`,
      payload,
      this.withProviderScope(),
    );
    return response.data;
  }

  async deleteSettings(provider: SocialOAuthProvider): Promise<void> {
    await this.dataSource.delete(
      `/api/v1/oauth/settings/${provider}`,
      this.withProviderScope(),
    );
  }
}

export const socialOAuthSettingsService = new SocialOAuthSettingsService();
